import { Button } from "@/components/ui/button";
import { SacChamado } from "@/types/Chamados";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, ChevronRight, Clock, FileText } from "lucide-react";

interface OpenCallCardProps {
  chamado: SacChamado;
  onViewDetails: () => void;
}

const getStatusInfo = (chamado: SacChamado) => {
  if (chamado.sst_descricao === "Encerrado") {
    return {
      label: "Concluído",
      color: "bg-green-100 text-green-700 border-green-200",
    };
  }

  if (chamado.sst_descricao === "Aguardando Atendimento") {
    return {
      label: "Aguardando atendimento",
      color: "bg-blue-100 text-blue-700 border-blue-200",
    };
  }

  if (chamado.sst_descricao === "Em Aberto") {
    return {
      label: "Em atendimento",
      color: "bg-amber-100 text-amber-700 border-amber-200",
    };
  }
  if (chamado.sst_descricao === "Concluído") {
    return {
      label: "Concluído",
      color: "bg-green-100 text-green-700 border-green-200",
    };
  }

  return {
    label: "Aberto",
    color: "bg-gray-100 text-gray-700 border-gray-200",
  };
};

export default function OpenCallCard({
  chamado,
  onViewDetails,
}: OpenCallCardProps) {
  const statusInfo = getStatusInfo(chamado);
  const dataAbertura = new Date(chamado.sac_data_abertura);
  const tempoRelativo = formatDistanceToNow(dataAbertura, {
    addSuffix: true,
    locale: ptBR,
  });

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm transition-all hover:shadow-md hover:border-n-green/30">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
              Protocolo #{chamado.sac_codigo}
            </span>
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}
            >
              {statusInfo.label}
            </span>
          </div>

          <p className="text-sm sm:text-base font-medium text-gray-900 line-clamp-2 break-words">
            {chamado.sac_descricao}
          </p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-n-green hover:bg-n-green/10 self-start sm:self-auto"
          onClick={onViewDetails}
        >
          <span className="hidden sm:inline">Ver detalhes</span>
          <span className="sm:hidden">Detalhes</span>
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-600 min-w-0">
          <Calendar className="h-4 w-4 text-n-green/70 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Aberto</p>
            <p className="font-medium text-gray-700 truncate">
              {tempoRelativo}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-gray-600 min-w-0">
          <Clock className="h-4 w-4 text-n-green/70 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Data do evento</p>
            <p className="font-medium text-gray-700 truncate">
              {format(new Date(chamado.sac_data_evento), "dd/MM/yyyy", {
                locale: ptBR,
              })}
            </p>
          </div>
        </div>

        {chamado.cve_codigo && (
          <div className="flex items-center gap-2 text-gray-600 min-w-0">
            <FileText className="h-4 w-4 text-n-green/70 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Cotação</p>
              <p className="font-medium text-gray-700 truncate">
                #{chamado.cve_codigo}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-n-green to-n-green/50 transform scale-x-0 transition-transform group-hover:scale-x-100" />
    </div>
  );
}
